"use client"

import { Head, router } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"

import { DestinationKindBadge } from "@/components/admin/onwards/destination-badges"
import { PaperOutro } from "@/components/player/shells/jacket/paper-outro"
import { type OutroSlate } from "@/components/player/shells/jacket/paper-types"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { staticCanonicalUrls } from "@/constants/static-canonical-urls"
import { DashboardLayout } from "@/layouts/dashboard-layout"
import { adminOnwardsLabPath } from "@/routes"

/** An excluded destination + why (see Onwards::SlateView.rejected_view). */
interface RejectedView {
  title: string
  kind: string
  reason: string
}

/** The Lab widens the player's slate with the governance `rejected` list. */
type LabSlate = OutroSlate & { rejected: RejectedView[] }

interface Controls {
  declaredAge: string | null
  timeZone: string
  date: string
  time: string
}

interface LabProps {
  playlists: { id: number; title: string; published: boolean }[]
  selectedPlaylistId: number | null
  playlistTitle: string | null
  creator: string | null
  slots: string[]
  selectedSlot: string
  timeZones: string[]
  controls: Controls
  playPath: string | null
  outroSlate: LabSlate | null
}

const SLOT_LABELS: Record<string, string> = {
  outro: "Outro (end of stack)",
  sidebar: "Sidebar",
  stalled_overlay: "Stalled overlay",
}

/**
 * One-click governance states — each chip writes the slot/age/time controls
 * (and nothing else, so they compose with any playlist/timezone/date). The
 * values mirror the rows of docs/tickets/onwards-lab-screenshot-index.md;
 * change them together.
 */
const PRESETS = [
  {
    label: "Adult, afternoon",
    patch: { slot: "outro", declaredAge: "25", time: "16:00" },
  },
  {
    label: "Under 15",
    patch: { slot: "outro", declaredAge: "14", time: "16:00" },
  },
  {
    label: "Late night (out of hours)",
    patch: { slot: "outro", declaredAge: "25", time: "23:00" },
  },
  {
    label: "Closing soon",
    patch: { slot: "outro", declaredAge: "25", time: "20:50" },
  },
  {
    label: "Empty slate",
    patch: { slot: "sidebar", declaredAge: "25", time: "16:00" },
  },
] as const

const REASON_LABELS: Record<string, string> = {
  gated: "Gated by age",
  closed: "Outside opening hours",
  unvalidated: "Unvalidated",
}

type ReasonColor = "warning" | "gray" | "error"
type ViewPort = "desktop" | "phone"

const REASON_COLORS: Record<string, ReasonColor> = {
  gated: "warning",
  closed: "gray",
  unvalidated: "error",
}

export default function OnwardsLab({
  playlists,
  selectedPlaylistId,
  playlistTitle,
  creator,
  slots,
  selectedSlot,
  timeZones,
  controls,
  playPath,
  outroSlate,
}: LabProps) {
  // Local state is authoritative for the controls; a debounced effect pushes
  // them to the server (which re-resolves + re-impresses the slate). Seeded
  // from props once — we don't re-sync, so typing never fights a round-trip.
  const [form, setForm] = useState({
    playlistId: selectedPlaylistId ? String(selectedPlaylistId) : "",
    slot: selectedSlot,
    declaredAge: controls.declaredAge ?? "",
    timeZone: controls.timeZone,
    date: controls.date,
    time: controls.time,
  })
  const mounted = useRef(false)

  useEffect(() => {
    // Skip the first render — the server already resolved these exact values.
    if (!mounted.current) {
      mounted.current = true
      return
    }
    const id = setTimeout(() => {
      router.get(
        adminOnwardsLabPath(),
        {
          playlist_id: form.playlistId,
          slot: form.slot,
          declared_age: form.declaredAge,
          time_zone: form.timeZone,
          date: form.date,
          time: form.time,
        },
        { preserveState: true, preserveScroll: true, replace: true },
      )
    }, 250)
    return () => clearTimeout(id)
  }, [form])

  const set = (patch: Partial<typeof form>) =>
    setForm((prev) => ({ ...prev, ...patch }))

  // A preset is "active" only when the exact trio matches — hand-edited
  // controls legitimately match no chip.
  const presetActive = (preset: (typeof PRESETS)[number]) =>
    form.slot === preset.patch.slot &&
    form.declaredAge === preset.patch.declaredAge &&
    form.time === preset.patch.time

  // Desktop-first: the Lab is a projector surface. Phone stays one click away
  // for the shell-width truth. Both mount the same PaperOutro — no fork.
  const [viewport, setViewport] = useState<ViewPort>("desktop")

  const rejected = outroSlate?.rejected ?? []

  return (
    <DashboardLayout>
      <Head title="Onwards Lab" />
      <main className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-4 pt-8 pb-16 lg:px-8">
        <header className="flex flex-col gap-1">
          <h1 className="text-primary font-display md:text-display-xs text-xl font-semibold">
            Onwards Lab
          </h1>
          <p className="text-md text-tertiary">
            Resolve an outro slate on its own. Set the context, see what gets
            excluded, then see the slate the player would show. It runs for
            real: the slate is impressed, and clicking a tile posts a referral.
          </p>
        </header>

        <div className="flex flex-col gap-8">
          {/* 1. Context: who's watching and when */}
          <section className="border-secondary flex flex-col gap-4 rounded-xl border p-5">
            <div className="flex flex-col gap-0.5">
              <h2 className="text-primary text-md font-semibold">Context</h2>
              <p className="text-tertiary text-sm">
                Set who is watching and when. This is what the slate resolves
                against.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  type="button"
                  size="sm"
                  color={presetActive(preset) ? "secondary" : "tertiary"}
                  aria-pressed={presetActive(preset)}
                  onClick={() => set(preset.patch)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Select
                  label="Playlist"
                  placeholder="Choose a playlist"
                  selectedKey={form.playlistId || null}
                  onChange={(key) =>
                    key != null && set({ playlistId: String(key) })
                  }
                  items={playlists.map((p) => ({
                    id: String(p.id),
                    label: p.published ? p.title : `${p.title} (draft)`,
                  }))}
                >
                  {(item) => (
                    <Select.Item key={item.id} id={item.id}>
                      {item.label}
                    </Select.Item>
                  )}
                </Select>

                <Select
                  label="Slot"
                  selectedKey={form.slot}
                  onChange={(key) => key != null && set({ slot: String(key) })}
                  items={slots.map((s) => ({
                    id: s,
                    label: SLOT_LABELS[s] ?? s,
                  }))}
                >
                  {(item) => (
                    <Select.Item key={item.id} id={item.id}>
                      {item.label}
                    </Select.Item>
                  )}
                </Select>

                <Input
                  label="Declared age"
                  type="number"
                  placeholder="blank = unknown (safe default)"
                  value={form.declaredAge}
                  onChange={(value) => set({ declaredAge: value })}
                />

                <Select
                  label="Timezone"
                  selectedKey={form.timeZone}
                  onChange={(key) =>
                    key != null && set({ timeZone: String(key) })
                  }
                  items={timeZones.map((tz) => ({ id: tz, label: tz }))}
                >
                  {(item) => (
                    <Select.Item key={item.id} id={item.id}>
                      {item.label}
                    </Select.Item>
                  )}
                </Select>

                <Input
                  label="Date"
                  type="date"
                  value={form.date}
                  onChange={(value) => set({ date: value })}
                />

                <Input
                  label="Time"
                  type="time"
                  value={form.time}
                  onChange={(value) => set({ time: value })}
                />
              </div>
              <p className="text-tertiary text-xs">
                Leave age blank and gated services drop (the safe default).
                Opening hours use the chosen timezone, so a closed service drops
                out and a 24/7 line takes its place.
              </p>
            </div>
          </section>

          {/* 2. Excluded: what dropped out and why */}
          <section className="border-secondary flex flex-col gap-3 rounded-xl border p-5">
            <div className="flex items-baseline justify-between">
              <div className="flex flex-col gap-0.5">
                <h2 className="text-primary text-md font-semibold">
                  Rejected (governance)
                </h2>
                <p className="text-tertiary text-sm">
                  Destinations placed on this playlist that this context ruled
                  out.
                </p>
              </div>
              <span className="text-tertiary text-xs tabular-nums">
                {rejected.length} excluded
              </span>
            </div>
            {rejected.length === 0 ? (
              <p className="text-tertiary text-sm">
                Nothing excluded. Every placed destination works for this
                context.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {rejected.map((row) => (
                  <li
                    key={`${row.kind}-${row.title}`}
                    className="border-secondary flex items-center gap-3 rounded-lg border px-3 py-2"
                  >
                    <span className="text-primary flex-1 text-sm font-medium">
                      {row.title}
                    </span>
                    <DestinationKindBadge kind={row.kind} />
                    <Badge
                      color={REASON_COLORS[row.reason] ?? "gray"}
                      size="sm"
                      type="pill-color"
                    >
                      {REASON_LABELS[row.reason] ?? row.reason}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* 3. Slate preview: the real PaperOutro, framed as the device the
              room is looking at — a browser window by default, the phone
              shell one click away. Same component both ways; only the frame
              (and its container width) changes. */}
          <section className="border-secondary flex flex-col items-center gap-4 rounded-xl border p-5">
            <div className="flex w-full flex-wrap items-start justify-between gap-3">
              <div className="flex flex-col gap-0.5">
                <h2 className="text-primary text-md font-semibold">
                  Slate preview
                </h2>
                <p className="text-tertiary text-sm">
                  The live outro render. Clicking a tile posts a real referral.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {(["desktop", "phone"] as const).map((option) => (
                  <Button
                    key={option}
                    type="button"
                    size="sm"
                    color={viewport === option ? "secondary" : "tertiary"}
                    aria-pressed={viewport === option}
                    onClick={() => setViewport(option)}
                    className="capitalize"
                  >
                    {option}
                  </Button>
                ))}
                {playPath && (
                  <a
                    href={playPath}
                    target="_blank"
                    rel="noreferrer"
                    className="text-tertiary hover:text-primary text-sm underline-offset-2 hover:underline"
                  >
                    open in the player ↗
                  </a>
                )}
              </div>
            </div>
            <PreviewPlatformSim viewport={viewport} playPath={playPath}>
              <PaperOutro
                key={`${form.playlistId}-${form.slot}-${viewport}`}
                playlistTitle={playlistTitle ?? "Playlist"}
                creator={creator ?? undefined}
                onReplay={() => undefined}
                reduce={false}
                wide={viewport === "desktop"}
                disabledInitAnimation
                disabledTriggerAutoPlay
                slate={outroSlate}
                cta={
                  <Button
                    data-outro-primary
                    type="button"
                    color="primary"
                    size="lg"
                    onClick={() =>
                      window.open(
                        staticCanonicalUrls.SIGN_UP_BEING_HERD,
                        "_blank",
                      )
                    }
                    className="w-full md:w-xs"
                  >
                    Sign up for Being Herd
                  </Button>
                }
              />
            </PreviewPlatformSim>
          </section>
        </div>
      </main>
    </DashboardLayout>
  )
}

/*
 * Preview platform simulator - A wrapper container for simulatring the viewport of desktop, mobile devices.
 */
interface PreviewPlatformSimProps {
  viewport?: ViewPort
  playPath?: string | null | undefined
  children: React.ReactNode
}
function PreviewPlatformSim({
  viewport = "desktop",
  playPath = "/playlists/…/play",
  children,
}: PreviewPlatformSimProps) {
  return (
    <div
      className={
        viewport === "desktop"
          ? "border-secondary w-full overflow-hidden rounded-xl border shadow-sm"
          : "border-secondary flex aspect-[9/19] w-full max-w-xs flex-col overflow-hidden rounded-2xl border"
      }
    >
      {viewport === "desktop" && (
        <div className="border-secondary bg-secondary flex items-center gap-3 border-b px-4 py-2.5">
          <div className="flex gap-1.5">
            <span className="bg-quaternary size-2.5 rounded-full" />
            <span className="bg-quaternary size-2.5 rounded-full" />
            <span className="bg-quaternary size-2.5 rounded-full" />
          </div>
          <span className="text-quaternary truncate font-mono text-xs">
            herdli.st{playPath}
          </span>
        </div>
      )}
      {viewport === "phone" && (
        <div className="bg-secondary flex h-7 shrink-0 items-center justify-center">
          <span className="bg-quaternary h-5 w-28 rounded-full" />
        </div>
      )}
      <div
        className={
          viewport === "desktop"
            ? "relative h-[640px] w-full"
            : "relative min-h-0 flex-1"
        }
      >
        {children}
      </div>
      {viewport === "phone" && (
        <div className="border-secondary bg-secondary flex shrink-0 flex-col items-center gap-1.5 border-t px-3 pt-1.5 pb-2">
          <span className="text-quaternary w-full truncate text-center font-mono text-[10px]">
            herdli.st{playPath}
          </span>
          <span className="bg-quaternary h-1 w-24 rounded-full" />
        </div>
      )}
    </div>
  )
}
